import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  User,  
  Shield, 
  FileText, 
  Mail, 
  LogOut,
  ChevronDown,
  Compass,
  HelpCircle,
  Star,
  Gem,
  BadgeCheck,
  BookOpen
} from "lucide-react";

import { useUserContext } from "@/context/AuthContext";
import { useSignOutAccount } from "@/lib/react-query/queries";
import NotificationDropdown from "../shared/NotificationDropdown";
import { getUserTrustScore } from "@/lib/appwrite/trustScore";

const Topbar = ({ scrollDir }: { scrollDir: "up" | "down" }) => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { mutate: signOut, isSuccess } = useSignOutAccount();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [trustScore, setTrustScore] = useState<number | null>(null);
  const [level, setLevel] = useState<string>('bronze');

  useEffect(() => {
    if (isSuccess) navigate(0);
  }, [isSuccess]);

  // Fetch trust score data when user changes
  useEffect(() => {
    if (user?.id) {
      const fetchTrustScoreData = async () => {
        try {
          const scoreData = await getUserTrustScore(user.id);
          setTrustScore(scoreData.score);
          setLevel(scoreData.level);
        } catch (error) {
          console.error('Error fetching trust score:', error);
        }
      };
      
      fetchTrustScoreData();
    }
  }, [user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatTrustScore = (score: number) => {
    if (score >= 1000) {
      return `${(score / 1000).toFixed(score % 1000 === 0 ? 0 : 1)}K`;
    }
    return score.toString();
  };

  const getLevelColor = () => {
    switch (level) {
      case 'bronze': return 'text-amber-500';
      case 'silver': return 'text-gray-300';
      case 'gold': return 'text-yellow-400';
      case 'diamond': return 'text-blue-300';
      case 'legend': return 'text-purple-500';
      default: return 'text-gray-300';
    }
  };

  const getLevelIcon = () => {
    switch (level) {
      case 'bronze': return <Star className="h-3.5 w-3.5" />;
      case 'silver': return <Star className="h-3.5 w-3.5" />;
      case 'gold': return <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />;
      case 'diamond': return <Gem className="h-3.5 w-3.5" />;
      case 'legend': return <BadgeCheck className="h-3.5 w-3.5" />;
      default: return <Star className="h-3.5 w-3.5" />;
    }
  };

  const menuItems = [
    {
      icon: User,
      label: "Profile",
      path: `/profile/${user.id}`,
      onClick: () => setIsProfileOpen(false)
    },
    {
      icon: Compass,
      label: "Explore",
      path: "/explore",
      onClick: () => setIsProfileOpen(false)
    },
    {
      icon: Shield,
      label: "Privacy Policy",
      path: "/privacy-policies",
      onClick: () => setIsProfileOpen(false)
    },
    {
      icon: FileText,
      label: "Terms & Conditions",
      path: "/termandcondition",
      onClick: () => setIsProfileOpen(false)
    },
    {
      icon: HelpCircle,
      label: "FAQ",
      path: "/faq",
      onClick: () => setIsProfileOpen(false)
    },
    {
      icon: BookOpen,
      label: "User Guide",
      path: "/busiconn-guide",
      onClick: () => setIsProfileOpen(false)
    }
  ];

  const handleSignOut = () => {
    setIsProfileOpen(false);
    signOut();
  };

  return (
    <section
      className={`topbar transition-transform duration-300 sticky top-0 w-full z-50 bg-dark-2 ${
        scrollDir === "down" ? "-translate-y-full" : "translate-y-0"
      }`}
    >
      <div className="flex-between py-4 px-5">
        <Link to="/" className="flex gap-3 items-center">
          <img
            src="/assets/images/busiconn.svg"
            alt="logo"
            className="w-28 md:w-36"
          />
        </Link>

        <div className="flex gap-4 items-center">
          {/* TrustScore Badge - Compact Version */}
          {trustScore !== null && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-800 rounded-full">
              <span className="text-xs font-medium text-light-3">TS:</span>
              <span className="text-sm font-semibold text-light-1">
                {formatTrustScore(trustScore)}
              </span>
              <span className={`${getLevelColor()}`}>
                {getLevelIcon()}
              </span>
            </div>
          )}

          {/* Notification bell */}
          <NotificationDropdown />

          {/* Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-2 p-1 rounded-full hover:bg-dark-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-2"
            >
              <img
                src={user.imageUrl || "/assets/icons/profile-placeholder.svg"}
                alt="profile"
                className="h-8 w-8 rounded-full object-cover border-2 border-transparent hover:border-primary-500 transition-all duration-200"
              />
              <ChevronDown 
                className={`h-4 w-4 text-light-3 transition-transform duration-200 ${
                  isProfileOpen ? "rotate-180" : ""
                }`} 
              />
            </button>

            {/* Dropdown Menu */}
            {isProfileOpen && (
              <div className="absolute right-0 mt-3 w-72 bg-dark-1 border border-dark-4 rounded-xl shadow-2xl overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200">
                {/* User Info Header */}
                <div className="px-4 py-4 bg-gradient-to-r from-primary-500/10 to-purple-500/10 border-b border-dark-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={user.imageUrl || "/assets/icons/profile-placeholder.svg"}
                      alt="profile"
                      className="h-12 w-12 rounded-full object-cover border-2 border-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-light-1 font-semibold truncate">
                        {user.name || "User"}
                      </p>
                      <p className="text-light-3 text-sm truncate">
                        {user.emailOrPhone.endsWith('@busiconn.com') ? user.emailOrPhone.replace('@busiconn.com', '') : user.emailOrPhone}
                      </p>
                      {trustScore !== null && (
                        <div className="flex items-center mt-1 space-x-2">
                          <div className="flex items-center gap-1 px-2 py-0.5 bg-dark-3 rounded-full text-xs">
                            <span className="text-light-3">TS:</span>
                            <span className="font-semibold text-light-1">
                              {formatTrustScore(trustScore)}
                            </span>
                            <span className={`${getLevelColor()}`}>
                              {getLevelIcon()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  {menuItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={item.onClick}
                      className="flex items-center gap-3 px-4 py-3 text-light-2 hover:bg-dark-3 hover:text-light-1 transition-all duration-200 group"
                    >
                      <item.icon className="h-5 w-5 text-light-3 group-hover:text-primary-500 transition-colors duration-200" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                  
                  {/* TrustScore Link */}
                  <Link
                    to="/trust-score"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-light-2 hover:bg-dark-3 hover:text-light-1 transition-all duration-200 group"
                  >
                    <div className="h-5 w-5 flex items-center justify-center text-light-3 group-hover:text-primary-500 transition-colors duration-200">
                      {getLevelIcon()}
                    </div>
                    <span className="font-medium">TrustScore Dashboard</span>
                  </Link>
                  
                  {/* Contact Us Section */}
                  <div className="px-4 py-3 border-t border-dark-4 mt-2">
                    <div className="flex items-center gap-3 mb-2">
                      <Mail className="h-5 w-5 text-primary-500" />
                      <span className="font-medium text-light-1">Contact Us</span>
                    </div>
                    <div className="ml-8">
                      <a 
                        href="mailto:contact@busiconn.com"
                        className="text-light-3 hover:text-primary-500 transition-colors duration-200 text-sm font-medium"
                      >
                        contact@busiconn.com
                      </a>
                    </div>
                  </div>
                  
                  {/* Divider */}
                  <div className="border-t border-dark-4 my-2"></div>
                  
                  {/* Logout Button */}
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-3 px-4 py-3 text-red hover:bg-orange-200 hover:text-orange-400 transition-all duration-200 w-full group"
                  >
                    <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    <span className="font-medium">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Topbar;